import React, { ReactElement, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import TopBarMenu from "./TopBarMenu";
import styles from "./messagesmenu.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MessageSimplifiedDTO from "features/messages/dto/MessageSimplifiedDTO";
import { MessageStatus } from "features/messages/enums/messageStatus";
import MessageModal from "features/messages/components/MessageModal";
import { useHistory } from "react-router-dom";
import IAppState from "infrastructure/store/IAppState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import { useSelector } from "react-redux";
import LinkButton from "features/shared/components/buttons/LinkButton";
import DateFormatter from "features/shared/components/formatters/DateFormatter";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import { useTranslation } from "react-i18next";

interface IProps {
  title: string;
  messages: MessageSimplifiedDTO[];
  onClickMessageItem: (message: MessageSimplifiedDTO) => void;
  messagesDropdownRef: React.MutableRefObject<any>;
}

const MessagesMenu: React.FC<IProps> = (props) => {
  // translations
  const tLabels = useTranslation("shared\\components").t;
  const tCategories = useTranslation("messages\\enums\\messageCategory").t;

  // STATE
  const [showModal, setShowModal] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState("");

  // STORE
  const history = useHistory();

  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;

  // HANDLERS
  const handleOnClickItem = (messageId: string) => {
    setSelectedMessageId(messageId);
    setShowModal(true);
  };

  const handleOnClickMessagesGridButton = () => {
    props.messagesDropdownRef.current.click();
    history.push("/messages");
  };

  // SUB-RENDERS
  const messageIcon = (isReaded: boolean): ReactElement => {
    if (isReaded) {
      return <FontAwesomeIcon icon="envelope-open" />;
    } else {
      return <FontAwesomeIcon icon="envelope" />;
    }
  };

  const renderMessageItems = (
    items: MessageSimplifiedDTO[]
  ): ReactElement[] => {
    return items.map((item) => {
      const isReaded = item.messageStatus === MessageStatus.Read;

      return (
        <Dropdown.Item
          href="#"
          key={item.messageId}
          className={`px-3 py-2 ${styles.messageItem} ${
            !isReaded ? styles.unreadMessage : ""
          }`}
          onClick={() => handleOnClickItem(item.messageId!)}
        >
          <div className="row no-gutters align-items-center">
            <div className="col-auto">
              <span className={styles.messageIcon}>
                {messageIcon(isReaded)}
              </span>
            </div>
            <div className="col pl-3">
              <h6 className={`${styles.messageTitle} text-truncate`}>
                {item.title}
              </h6>
              <div className="text-truncate text-muted small">
                <DateFormatter date={item.createdOn!} /> |{" "}
                {tCategories(item.messageCategory!)}
              </div>
            </div>
          </div>
        </Dropdown.Item>
      );
    });
  };

  const noContentRender = () => {
    return <div className="p-3 text-muted">{tLabels("messagesMenu.noContent")}</div>;
  };

  // RENDER
  return (
    <TopBarMenu title={props.title}>
      <NoContentWrapper
        data={props.messages}
        loadStatus="AfterFirstLoad"
        noContentRender={noContentRender}
      >
        <div className="pt-2">
          {renderMessageItems(props.messages)}
          <div className="text-center mb-3 mt-1">
            <LinkButton
              onClick={handleOnClickMessagesGridButton}
            />
          </div>
        </div>
        {selectedMessageId && (
          <MessageModal
            selectedMessageId={selectedMessageId}
            customerId={customer.customerId!}
            show={showModal}
            setShow={setShowModal}
          />
        )}
      </NoContentWrapper>
    </TopBarMenu>
  );
};

export default MessagesMenu;
